import React, { useState } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';

const TrialSignupModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    company: '',
    companySize: '',
    industry: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate email
      if (!formData.email || !formData.email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Validate step 2
      if (!formData.fullName || !formData.company) {
        setError('Please fill in all required fields');
        return;
      }
      setStep(3);
    } else {
      // Final submission
      setLoading(true);
      setError('');
      
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/trial-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        const data = await response.json();
        
        if (response.ok) {
          setSuccess(true);
          setTimeout(() => {
            onClose();
            // Reset form
            setFormData({
              email: '',
              fullName: '',
              company: '',
              companySize: '',
              industry: '',
              phone: ''
            });
            setStep(1);
            setSuccess(false);
          }, 3000);
        } else {
          setError(data.error || 'Something went wrong. Please try again.');
        }
      } catch (err) {
        setError('Failed to connect. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#111827] border border-[#2563EB]/30 rounded-2xl shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {success ? (
          // Success State
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-3xl font-bold text-[#F8FAFC] mb-4">Welcome to PriceStrike!</h3>
            <p className="text-[#94A3B8] text-lg">
              Your free trial is ready. Check your email for next steps.
            </p>
          </div>
        ) : (
          <div className="p-8">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">
                Start Your Free Trial
              </h2>
              <p className="text-[#94A3B8]">
                14 days free. No credit card required.
              </p>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                        step >= s
                          ? 'bg-[#2563EB] text-white'
                          : 'bg-[#2563EB]/20 text-[#94A3B8]'
                      }`}
                    >
                      {s}
                    </div>
                    <span className="text-xs text-[#94A3B8] mt-1">
                      {s === 1 ? 'Email' : s === 2 ? 'Details' : 'Finish'}
                    </span>
                  </div>
                  {s < 3 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      step > s ? 'bg-[#2563EB]' : 'bg-[#2563EB]/20'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                      required
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                      required
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Company Size
                    </label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="500+">500+ employees</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Industry
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                    >
                      <option value="">Select industry</option>
                      <option value="retail">Retail & E commerce</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="tech">Tech & SaaS</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="services">Professional Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#F8FAFC] font-medium mb-2">
                      Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-4 py-3 bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <Button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    variant="outline"
                    className="flex-1 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10 py-3"
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-3"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : step === 3 ? (
                    'Start Free Trial'
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </form>

            <p className="text-xs text-[#94A3B8] text-center mt-6">
              By signing up, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialSignupModal;